import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Hourglass, CalendarDays, Activity, ArrowRight, Plus, Wallet, Calculator } from "lucide-react";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";

function StatCard({ icon: Icon, label, value, accent, testid }) {
  return (
    <div
      className="bg-[#0e0e0e] border border-white/10 p-6 hover:border-[#C9963F]/40 transition-colors fade-up"
      data-testid={testid}
    >
      <div className="flex items-start justify-between">
        <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">{label}</div>
        <div className={`p-2 ${accent ? "text-[#C9963F]" : "text-white/40"}`}>
          <Icon size={16} strokeWidth={1.5} />
        </div>
      </div>
      <div className="font-serif-brand text-4xl sm:text-5xl mt-3 text-white break-all" data-testid={`${testid}-value`}>
        {value}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [data, setData] = useState({
    total_contracts: 0,
    pending_signatures: 0,
    upcoming_events: 0,
    outstanding_balance: 0,
    recent_activity: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/dashboard");
        setData(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Default range: from 1st of this month to last day of next month
  const today = new Date();
  const fromDefault = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
  const toDefault = new Date(today.getFullYear(), today.getMonth() + 2, 0).toISOString().slice(0, 10);

  const [revFrom, setRevFrom] = useState(fromDefault);
  const [revTo, setRevTo] = useState(toDefault);
  const [revData, setRevData] = useState(null);
  const [revLoading, setRevLoading] = useState(false);

  async function loadRevenue(from, to) {
    setRevLoading(true);
    try {
      const { data } = await api.get(`/admin/revenue?from_date=${from}&to_date=${to}`);
      setRevData(data);
    } catch {
      setRevData(null);
    } finally {
      setRevLoading(false);
    }
  }

  useEffect(() => { loadRevenue(revFrom, revTo); }, []); // initial load

  function setQuickRange(months) {
    const f = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
    const t = new Date(today.getFullYear(), today.getMonth() + months, 0).toISOString().slice(0, 10);
    setRevFrom(f); setRevTo(t); loadRevenue(f, t);
  }

  return (
    <div data-testid="admin-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div className="fade-up">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9963F] mb-2">Overview</div>
          <h1 className="font-serif-brand text-4xl sm:text-5xl text-white tracking-tight">
            Dashboard
          </h1>
          <p className="text-white/50 mt-2 text-sm">
            Manage contracts, signatures, and upcoming events.
          </p>
        </div>
        <Link
          to="/admin/contracts/new"
          data-testid="new-contract-btn"
          className="inline-flex items-center gap-2 bg-[#C9963F] text-black px-5 py-3 text-sm font-medium hover:bg-[#A67A2D] transition-colors self-start sm:self-auto"
        >
          <Plus size={16} /> New Contract
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-10">
        <StatCard
          icon={FileText}
          label="Total Contracts"
          value={loading ? "—" : data.total_contracts}
          testid="stat-total"
          accent
        />
        <StatCard
          icon={Hourglass}
          label="Pending Signatures"
          value={loading ? "—" : data.pending_signatures}
          testid="stat-pending"
        />
        <StatCard
          icon={CalendarDays}
          label="Upcoming (30 days)"
          value={loading ? "—" : data.upcoming_events}
          testid="stat-upcoming"
        />
        <StatCard
          icon={Wallet}
          label="Outstanding Balance"
          value={loading ? "—" : `$${Number(data.outstanding_balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          testid="stat-outstanding"
          accent
        />
      </div>

      {/* Revenue by date range */}
      <div className="bg-[#0e0e0e] border border-white/10 fade-up mb-10" data-testid="revenue-section">
        <div className="p-5 border-b border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2">
            <Calculator size={16} className="text-[#C9963F]" strokeWidth={1.5} />
            <h2 className="text-sm uppercase tracking-[0.2em] text-white/80">Revenue by Date Range</h2>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1 text-xs text-white/50">
              <span className="hidden sm:inline">From</span>
              <input type="date" value={revFrom} onChange={(e) => setRevFrom(e.target.value)} data-testid="revenue-from"
                className="bg-[#0a0a0a] border border-white/10 px-2 py-1 text-xs text-white focus:border-[#C9963F] focus:outline-none" />
              <span>→</span>
              <input type="date" value={revTo} onChange={(e) => setRevTo(e.target.value)} data-testid="revenue-to"
                className="bg-[#0a0a0a] border border-white/10 px-2 py-1 text-xs text-white focus:border-[#C9963F] focus:outline-none" />
            </div>
            <button onClick={() => loadRevenue(revFrom, revTo)} data-testid="revenue-apply"
              className="bg-[#C9963F] text-black px-3 py-1.5 text-xs font-medium hover:bg-[#A67A2D]">Apply</button>
            <div className="flex gap-1">
              <button onClick={() => setQuickRange(1)} className="text-[10px] uppercase tracking-wider border border-white/15 text-white/60 hover:border-white/40 px-2 py-1">This month</button>
              <button onClick={() => setQuickRange(3)} className="text-[10px] uppercase tracking-wider border border-white/15 text-white/60 hover:border-white/40 px-2 py-1">90 days</button>
              <button onClick={() => setQuickRange(12)} className="text-[10px] uppercase tracking-wider border border-white/15 text-white/60 hover:border-white/40 px-2 py-1">YTD</button>
            </div>
          </div>
        </div>

        {revData && (
          <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-white/5">
            <div className="p-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">Gross Total</div>
              <div className="font-serif-brand text-3xl text-white mt-2" data-testid="rev-gross">${revData.gross_total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
              <div className="text-[10px] uppercase tracking-wider text-white/40 mt-1">{revData.contracts_count} contract{revData.contracts_count !== 1 ? "s" : ""}</div>
            </div>
            <div className="p-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">Received</div>
              <div className="font-serif-brand text-3xl text-[#C9963F] mt-2" data-testid="rev-received">${revData.received.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
              <div className="text-[10px] uppercase tracking-wider text-[#C9963F]/70 mt-1">
                {revData.gross_total > 0 ? Math.round((revData.received / revData.gross_total) * 100) : 0}% collected
              </div>
            </div>
            <div className="p-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">Pending</div>
              <div className="font-serif-brand text-3xl text-amber-300 mt-2" data-testid="rev-pending">${revData.pending.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
              <div className="text-[10px] uppercase tracking-wider text-amber-300/70 mt-1">Outstanding</div>
            </div>
          </div>
        )}

        {revData && revData.rows.length > 0 && (
          <div className="border-t border-white/10 max-h-72 overflow-y-auto" data-testid="revenue-rows">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[#0e0e0e]">
                <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-white/50">
                  <th className="px-5 py-2 font-medium">Event Date</th>
                  <th className="px-5 py-2 font-medium">Client</th>
                  <th className="px-5 py-2 font-medium text-right">Total</th>
                  <th className="px-5 py-2 font-medium text-right">Received</th>
                  <th className="px-5 py-2 font-medium text-right">Pending</th>
                </tr>
              </thead>
              <tbody>
                {revData.rows.map((r) => (
                  <tr key={r.id} className="border-t border-white/5">
                    <td className="px-5 py-2 text-white/70 text-xs">{new Date(r.event_date + "T00:00:00").toLocaleDateString()}</td>
                    <td className="px-5 py-2 text-white"><Link to={`/admin/contracts/${r.id}`} className="hover:text-[#C9963F]">{r.client_name}</Link></td>
                    <td className="px-5 py-2 text-right text-white">${r.total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                    <td className="px-5 py-2 text-right text-[#C9963F]">${r.amount_paid.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                    <td className="px-5 py-2 text-right text-amber-300">${r.amount_outstanding.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {revData && revData.rows.length === 0 && (
          <div className="p-8 text-center text-white/40 text-sm">No contracts in this range.</div>
        )}
        {revLoading && <div className="p-4 text-white/40 text-xs">Loading…</div>}
      </div>

      <div className="bg-[#0e0e0e] border border-white/10 fade-up">
        <div className="p-5 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity size={16} className="text-[#C9963F]" strokeWidth={1.5} />
            <h2 className="text-sm uppercase tracking-[0.2em] text-white/80">Recent Activity</h2>
          </div>
          <Link
            to="/admin/contracts"
            className="text-xs text-white/60 hover:text-[#C9963F] flex items-center gap-1"
            data-testid="view-all-contracts"
          >
            View all <ArrowRight size={14} />
          </Link>
        </div>
        <div className="divide-y divide-white/5" data-testid="activity-feed">
          {(data.recent_activity || []).length === 0 && (
            <div className="p-8 text-center text-white/40 text-sm">
              No activity yet. Create your first contract to get started.
            </div>
          )}
          {(data.recent_activity || []).map((a) => (
            <div key={a.id} className="p-5 flex items-start gap-4 hover:bg-white/[0.02] transition-colors">
              <div className="w-1 h-10 bg-[#C9963F]/40" />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white">{a.action}</div>
                {a.client_name && <div className="text-xs text-white/50 mt-0.5">{a.client_name}</div>}
                {a.detail && <div className="text-xs text-white/40 mt-1 truncate">{a.detail}</div>}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-white/30 whitespace-nowrap">
                {new Date(a.created_at).toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
